import { createActionGroup, emptyProps, props } from '@ngrx/store';

import { SafeAny } from '../../types/safe-any';

import {
  CANumberById,
  Customer,
  ExportQuotesBundleResponse,
  ICableAssemblyOrder,
  Quote,
  QuotePerson,
  QuoteStatus,
} from './quotes.state.interface';

export const QuotesActions = createActionGroup({
  source: 'Quotes',
  events: {
    // Load Active Quotes
    'Load Active Quotes': props<{ payload: SafeAny }>(),
    'Load Active Quotes Success': props<{ activeQuotes: Quote[]; totalCount: number }>(),
    'Load Active Quotes Failure': props<{ error: string }>(),

    // Load Archived Quotes
    'Load Archived Quotes': props<{ payload: SafeAny }>(),
    'Load Archived Quotes Success': props<{ archivedQuotes: Quote[]; totalCount: number }>(),
    'Load Archived Quotes Failure': props<{ error: string }>(),

    // Load Assignees
    'Load Assignees': props<{ payload: SafeAny }>(),
    'Load Assignees Success': props<{ assignees: QuotePerson[] }>(),
    'Load Assignees Failure': props<{ error: string }>(),

    // Load Customers
    'Load Customers': emptyProps(),
    'Load Customers Success': props<{ customers: Customer[] }>(),
    'Load Customers Failure': props<{ error: string }>(),

    // Load Statuses
    'Load Statuses': props<{ payload: SafeAny }>(),
    'Load Statuses Success': props<{ statuses: QuoteStatus[] }>(),
    'Load Statuses Failure': props<{ error: string }>(),

    // Load Shipping Methods
    'Load Shipping Methods': props<{ payload: SafeAny }>(),
    'Load Shipping Methods Success': props<{ shippingMethods: SafeAny[] }>(),
    'Load Shipping Methods Failure': props<{ error: string }>(),

    // Add Quote
    'Add Quote': props<{ payload: ICableAssemblyOrder }>(),
    'Add Quote Success': props<{ quote: ICableAssemblyOrder }>(),
    'Add Quote Failure': props<{ error: string }>(),

    // Update Quote
    'Update Quote': props<{
      quoteId: string;
      payload: ICableAssemblyOrder;
      initiateRedirect: boolean;
    }>(),
    'Update Quote Success': props<{ quote: ICableAssemblyOrder; initiateRedirect: boolean }>(),
    'Update Quote Failure': props<{ error: string }>(),

    // Delete Quote
    'Delete Quote': props<{ id: string }>(),
    'Delete Quote Success': emptyProps(),
    'Delete Quote Failure': props<{ error: string }>(),

    // Select Quote
    'Select Quote': props<{ quoteId: string }>(),

    // Update Filter
    'Update Filter': props<{ filter: Partial<SafeAny> }>(),

    // Update Sorting
    'Update Order': props<{ order: string }>(),

    // Pagination
    'Update Page': props<{ page: number; limit: number }>(),

    // Archive Status
    'Toggle Archive Status': props<{ quoteId: string; archived: boolean }>(),
    'Toggle Archive Status Success': props<{ quoteId: string; archived: boolean }>(),
    'Toggle Archive Status Failure': props<{ error: string }>(),

    // Send to Production
    'Send To Production': props<{
      quoteId: string;
      productionData: {
        document?: string;
        purchaseOrderNumber?: string;
        quantity?: number;
        attachedOrder?: string;
      };
    }>(),
    'Send To Production Success': props<{ quoteId: string }>(),
    'Send To Production Failure': props<{ error: string }>(),

    // Load CA Number Details
    'Load CA Number Details': props<{ quoteId: string; caNumber: string }>(),
    'Load CA Number Details Success': props<{
      quoteId: string;
      details: {
        caNumber: string;
        description?: string;
        customQuoteDescription?: string;
      };
    }>(),
    'Load CA Number Details Failure': props<{ error: string }>(),

    // Load Quote Shipping Method
    'Load Quote Shipping Method': emptyProps(),
    'Load Quote Shipping Method Success': props<{
      quotesShippingMethod: SafeAny;
    }>(),
    'Load Quote Shipping Method Failure': props<{ error: string }>(),

    // Load CA Numbers
    'Load CA Numbers': props<{ payload: SafeAny }>(),
    'Load CA Numbers Success': props<{ caNumbers: SafeAny[] }>(),
    'Load CA Numbers Failure': props<{ error: string }>(),

    // Load CA Number By Id
    'Load CA Number By Id': props<{ id: string; includeRelations: boolean }>(),
    'Load CA Number By Id Success': props<{ caNumberById: CANumberById }>(),
    'Load CA Number By Id Failure': props<{ error: string }>(),

    // Build Quote
    'Build Quote': props<{
      quote: Partial<SafeAny>;
    }>(),
    'Build Quote Success': props<{ quote: SafeAny }>(),
    'Build Quote Failure': props<{ error: string }>(),

    // Create Quote
    'Create Quote': props<{ payload: ICableAssemblyOrder }>(),
    'Create Quote Success': props<{ createdQuote: Quote }>(),
    'Create Quote Failure': props<{ error: string }>(),

    // Export Quotes Bundle
    'Export Quotes Bundle': props<{ quotesId: string }>(),
    'Export Quotes Bundle Success': props<{ response: ExportQuotesBundleResponse }>(),
    'Export Quotes Bundle Failure': props<{ error: string }>(),

    // Load All Item Processes
    'Load Item Processes': props<{ payload: SafeAny }>(),
    'Load Item Processes Success': props<{ itemProcesses: SafeAny[] }>(),
    'Load Item Processes Failure': props<{ error: string }>(),

    // Load Specific Item Process
    'Load Specific Item Process': props<{ id: string }>(),
    'Load Specific Item Process Success': props<{ selectedItemProcess: SafeAny }>(),
    'Load Specific Item Process Failure': props<{ error: string }>(),

    // Load Specific Quote
    'Load Specific Quote': props<{ quoteId: number }>(),
    'Load Specific Quote Success': props<{ selectedQuote: SafeAny }>(),
    'Load Specific Quote Failure': props<{ error: string }>(),

    // Load Item Processes Categories
    'Load Item Processes Categories': emptyProps(),
    'Load Item Processes Categories Success': props<{ itemProcessesCategories: SafeAny }>(),
    'Load Item Processes Categories Failure': props<{ error: string }>(),

    // Clear State
    'Clear State': emptyProps(),
    'Clear Item Process State': emptyProps(),
    'Clear Selected CANumber': emptyProps(),
    'Clear Selected Quote': emptyProps(),

    // Save Note
    'Save Note': props<{ quoteId: string; notes: string }>(),
    'Save Note Success': props<{ savedNote: SafeAny }>(),
    'Save Note Failure': props<{ error: string }>(),

    // Clear Note
    'Clear Note': emptyProps(),

    // Archive Quote
    'Archive Quote': props<{ id: string; archived: boolean }>(),
    'Archive Quote Success': props<{ archivedQuote: Quote }>(),
    'Archive Quote Failure': props<{ error: string }>(),

    // Unarchive Quote
    'Unarchive Quote': props<{ id: string; archived: boolean }>(),
    'Unarchive Quote Success': props<{ unarchivedQuote: Quote }>(),
    'Unarchive Quote Failure': props<{ error: string }>(),

    // Duplicate Quote
    'Duplicate Quote': props<{ quoteId: string }>(),
    'Duplicate Quote Success': props<{ quoteId: { id: number } }>(),
    'Duplicate Quote Failure': props<{ error: string }>(),
  },
});
